import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Plus, Trash2, X, Save, CheckCircle2, XCircle, Clock, MessageCircle, CheckCheck, Filter } from "lucide-react";
import AdminLayout from "./AdminLayout";
import { useI18n } from "../../i18n/I18nContext";
import { api, formatApiError } from "../../lib/api";

const STATUS_META = {
    pending: { color: "bg-brand-gold text-white", label_fr: "En attente", label_en: "Pending" },
    confirmed: { color: "bg-brand-emerald text-white", label_fr: "Confirmée", label_en: "Confirmed" },
    completed: { color: "bg-brand-navy text-white", label_fr: "Terminée", label_en: "Completed" },
    cancelled: { color: "bg-gray-400 text-white", label_fr: "Annulée", label_en: "Cancelled" },
};

export default function AdminBookings() {
    const { t, lang } = useI18n();
    const [slots, setSlots] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [filters, setFilters] = useState({ status: "", type: "" });
    const [newSlot, setNewSlot] = useState({ date: "", start: "", end: "", label: "" });
    const [selected, setSelected] = useState(null);
    const [draft, setDraft] = useState({ status: "pending", admin_note: "" });

    const loadSlots = () => api.get("/admin/meeting-slots").then((r) => setSlots(r.data)).catch(() => setSlots([]));
    const loadBookings = () => {
        const params = {};
        if (filters.status) params.status = filters.status;
        if (filters.type) params.type = filters.type;
        return api.get("/admin/bookings", { params }).then((r) => setBookings(r.data)).catch(() => setBookings([]));
    };

    useEffect(() => { loadSlots(); }, []);
    useEffect(() => { loadBookings(); }, [filters]);

    const addSlot = async () => {
        if (!newSlot.date || !newSlot.start || !newSlot.end) return;
        try {
            await api.post("/admin/meeting-slots", newSlot);
            toast.success(t("bookings_admin.saved"));
            setNewSlot({ date: "", start: "", end: "", label: "" });
            loadSlots();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const removeSlot = async (id) => {
        if (!window.confirm(t("admin.confirm_delete"))) return;
        try {
            await api.delete(`/admin/meeting-slots/${id}`);
            toast.success(t("bookings_admin.deleted"));
            loadSlots();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const openDrawer = (b) => {
        setSelected(b);
        setDraft({ status: b.status, admin_note: b.admin_note || "" });
    };

    const saveDrawer = async () => {
        if (!selected) return;
        try {
            const { data } = await api.put(`/admin/bookings/${selected.id}`, draft);
            toast.success(t("bookings_admin.saved"));
            setBookings((prev) => prev.map((b) => (b.id === data.id ? data : b)));
            setSelected(data);
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const removeBooking = async (id) => {
        if (!window.confirm(t("admin.confirm_delete"))) return;
        try {
            await api.delete(`/admin/bookings/${id}`);
            toast.success(t("bookings_admin.deleted"));
            setBookings((prev) => prev.filter((b) => b.id !== id));
            if (selected?.id === id) setSelected(null);
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const labelStatus = (s) => STATUS_META[s]?.[lang === "en" ? "label_en" : "label_fr"] || s;

    const waUrl = (b) => {
        const num = (b.whatsapp || b.phone || "").replace(/[^0-9]/g, "");
        if (!num) return null;
        const link = `${window.location.origin}/reservation/suivi/${b.tracking_token}`;
        const type = b.type === "meeting" ? "votre rendez-vous" : "votre réservation";
        const statusLabel = labelStatus(b.status);
        const text = `Bonjour ${b.name}, GAB'S SERVICES : ${type} est ${statusLabel}. Suivi : ${link}`;
        return `https://wa.me/${num}?text=${encodeURIComponent(text)}`;
    };

    return (
        <AdminLayout>
            <div className="p-6 lg:p-10 space-y-10">
                <header>
                    <div className="text-[0.65rem] tracking-[0.28em] uppercase font-bold text-brand-gold mb-2">{t("admin.dashboard")}</div>
                    <h1 className="font-display text-3xl text-brand-navy">{t("bookings_admin.title")}</h1>
                </header>

                {/* SLOTS */}
                <section data-testid="slots-panel">
                    <div className="flex items-end justify-between gap-4 mb-4 flex-wrap">
                        <h2 className="font-display text-2xl text-brand-navy">{t("bookings_admin.slots_title")}</h2>
                    </div>
                    <div className="bg-white border border-gray-100 p-5 grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3" data-testid="new-slot-form">
                        <label className="block">
                            <span className="text-[0.62rem] tracking-[0.22em] uppercase font-bold text-brand-navy">{t("bookings_admin.slot_date")}</span>
                            <input type="date" value={newSlot.date} onChange={(e) => setNewSlot({ ...newSlot, date: e.target.value })} data-testid="new-slot-date" className="w-full mt-1 border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-brand-gold" />
                        </label>
                        <label className="block">
                            <span className="text-[0.62rem] tracking-[0.22em] uppercase font-bold text-brand-navy">{t("bookings_admin.slot_start")}</span>
                            <input type="time" value={newSlot.start} onChange={(e) => setNewSlot({ ...newSlot, start: e.target.value })} data-testid="new-slot-start" className="w-full mt-1 border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-brand-gold" />
                        </label>
                        <label className="block">
                            <span className="text-[0.62rem] tracking-[0.22em] uppercase font-bold text-brand-navy">{t("bookings_admin.slot_end")}</span>
                            <input type="time" value={newSlot.end} onChange={(e) => setNewSlot({ ...newSlot, end: e.target.value })} data-testid="new-slot-end" className="w-full mt-1 border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-brand-gold" />
                        </label>
                        <label className="block col-span-2 sm:col-span-1">
                            <span className="text-[0.62rem] tracking-[0.22em] uppercase font-bold text-brand-navy">{t("bookings_admin.slot_label")}</span>
                            <input value={newSlot.label} onChange={(e) => setNewSlot({ ...newSlot, label: e.target.value })} data-testid="new-slot-label" className="w-full mt-1 border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-brand-gold" />
                        </label>
                        <button onClick={addSlot} data-testid="new-slot-add" className="mt-4 sm:mt-0 sm:self-end inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-emerald hover:bg-brand-emerald-soft text-white text-[0.7rem] tracking-[0.22em] uppercase font-semibold transition-colors">
                            <Plus className="w-4 h-4" /> {t("bookings_admin.add")}
                        </button>
                    </div>
                    <div className="bg-white border border-gray-100 overflow-x-auto" data-testid="slots-table">
                        {slots.length === 0 ? (
                            <div className="py-8 text-center text-gray-500 text-sm">Aucun créneau publié.</div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead className="bg-brand-paper text-[0.62rem] tracking-[0.18em] uppercase text-brand-navy">
                                    <tr>
                                        <th className="text-left px-4 py-2.5">{t("bookings_admin.slot_date")}</th>
                                        <th className="text-left px-4 py-2.5">{t("bookings_admin.slot_start")}</th>
                                        <th className="text-left px-4 py-2.5">{t("bookings_admin.slot_end")}</th>
                                        <th className="text-left px-4 py-2.5">{t("bookings_admin.slot_label")}</th>
                                        <th className="text-left px-4 py-2.5"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {slots.map((s) => (
                                        <tr key={s.id} className="border-t border-gray-100" data-testid={`slot-row-${s.id}`}>
                                            <td className="px-4 py-2.5">{s.date}</td>
                                            <td className="px-4 py-2.5">{s.start}</td>
                                            <td className="px-4 py-2.5">{s.end}</td>
                                            <td className="px-4 py-2.5 text-gray-500">{s.label}</td>
                                            <td className="px-4 py-2.5 text-right">
                                                <button onClick={() => removeSlot(s.id)} data-testid={`slot-delete-${s.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-500 hover:bg-red-600 text-white text-xs uppercase">
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </section>

                {/* BOOKINGS */}
                <section>
                    <div className="flex items-end justify-between gap-4 mb-4 flex-wrap">
                        <h2 className="font-display text-2xl text-brand-navy">{t("bookings_admin.title")}</h2>
                        <div className="flex items-end gap-3" data-testid="bookings-filters">
                            <Sel label={t("inquiries.filters.status")} value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} testid="filter-status"
                                options={[
                                    { v: "", l: t("inquiries.filters.all") },
                                    { v: "pending", l: labelStatus("pending") },
                                    { v: "confirmed", l: labelStatus("confirmed") },
                                    { v: "completed", l: labelStatus("completed") },
                                    { v: "cancelled", l: labelStatus("cancelled") },
                                ]} />
                            <Sel label={t("inquiries.filters.type")} value={filters.type} onChange={(v) => setFilters((f) => ({ ...f, type: v }))} testid="filter-type"
                                options={[
                                    { v: "", l: t("inquiries.filters.all") },
                                    { v: "rental", l: t("bookings_admin.rental") },
                                    { v: "meeting", l: t("bookings_admin.meeting") },
                                ]} />
                        </div>
                    </div>
                    <div className="bg-white border border-gray-100 overflow-x-auto" data-testid="bookings-table">
                        <table className="w-full text-sm">
                            <thead className="bg-brand-paper text-[0.62rem] tracking-[0.18em] uppercase text-brand-navy">
                                <tr>
                                    <th className="text-left px-4 py-2.5">{t("bookings_admin.columns.date")}</th>
                                    <th className="text-left px-4 py-2.5">{t("bookings_admin.columns.client")}</th>
                                    <th className="text-left px-4 py-2.5">{t("bookings_admin.columns.type")}</th>
                                    <th className="text-left px-4 py-2.5">{t("bookings_admin.columns.details")}</th>
                                    <th className="text-left px-4 py-2.5">{t("bookings_admin.columns.status")}</th>
                                    <th className="text-left px-4 py-2.5">{t("bookings_admin.columns.actions")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bookings.length === 0 ? (
                                    <tr><td colSpan={6} className="text-center py-10 text-gray-500">{t("bookings_admin.empty")}</td></tr>
                                ) : bookings.map((b) => (
                                    <tr key={b.id} className="border-t border-gray-100 hover:bg-brand-paper/60 transition-colors" data-testid={`booking-row-${b.id}`}>
                                        <td className="px-4 py-2.5 whitespace-nowrap text-gray-500">{new Date(b.created_at).toLocaleDateString()}</td>
                                        <td className="px-4 py-2.5">
                                            <div className="font-semibold text-brand-navy">{b.name}</div>
                                            <div className="text-xs text-gray-500">{b.email}</div>
                                        </td>
                                        <td className="px-4 py-2.5">{b.type === "meeting" ? t("bookings_admin.meeting") : t("bookings_admin.rental")}</td>
                                        <td className="px-4 py-2.5 text-xs text-gray-700 max-w-[280px]">
                                            {b.type === "rental" ? `${b.property_title || ""} · ${b.check_in} → ${b.check_out}` : `${b.meeting_date} ${b.meeting_start}-${b.meeting_end}`}
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <span className={`inline-block px-2.5 py-0.5 text-[0.62rem] tracking-[0.22em] uppercase font-bold ${STATUS_META[b.status]?.color}`}>{labelStatus(b.status)}</span>
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <div className="flex items-center gap-2">
                                                <button onClick={() => openDrawer(b)} data-testid={`booking-open-${b.id}`} className="px-3 py-1.5 bg-brand-navy hover:bg-brand-navy-soft text-white text-xs uppercase tracking-wide">Détails</button>
                                                <button onClick={() => removeBooking(b.id)} data-testid={`booking-delete-${b.id}`} className="px-2.5 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs"><Trash2 className="w-3.5 h-3.5" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>

            <AnimatePresence>
                {selected && (
                    <>
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/40 z-40" onClick={() => setSelected(null)} />
                        <motion.aside initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 24, stiffness: 220 }} className="fixed top-0 right-0 h-screen w-full sm:w-[520px] bg-white z-50 shadow-2xl overflow-y-auto" data-testid="booking-drawer">
                            <header className="sticky top-0 bg-brand-navy text-white px-5 py-4 flex items-center justify-between z-10">
                                <div>
                                    <div className="text-[0.62rem] tracking-[0.24em] uppercase font-bold text-brand-gold">
                                        {selected.type === "meeting" ? t("bookings_admin.meeting") : t("bookings_admin.rental")} · #{selected.id.slice(0, 8)}
                                    </div>
                                    <h3 className="font-display text-xl mt-0.5">{selected.name}</h3>
                                </div>
                                <button onClick={() => setSelected(null)} className="w-9 h-9 bg-white/10 hover:bg-brand-gold inline-flex items-center justify-center" data-testid="drawer-close">
                                    <X className="w-4 h-4" />
                                </button>
                            </header>
                            <div className="p-5 space-y-5 pb-24">
                                <KV k="Email" v={<a href={`mailto:${selected.email}`} className="text-brand-emerald hover:underline">{selected.email}</a>} />
                                <KV k="Téléphone" v={selected.phone || "—"} />
                                {(selected.whatsapp || selected.phone) ? (
                                    <KV k="WhatsApp" v={
                                        <a href={`https://wa.me/${(selected.whatsapp || selected.phone).replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#25D366]">
                                            <MessageCircle className="w-3.5 h-3.5" /> {selected.whatsapp || selected.phone}
                                        </a>
                                    } />
                                ) : null}
                                {selected.type === "rental" ? (
                                    <>
                                        <KV k="Bien" v={selected.property_title || selected.property_id} />
                                        <KV k="Arrivée" v={selected.check_in} />
                                        <KV k="Départ" v={selected.check_out} />
                                        <KV k="Personnes" v={selected.guests} />
                                    </>
                                ) : (
                                    <>
                                        <KV k="Date" v={selected.meeting_date} />
                                        <KV k="Horaire" v={`${selected.meeting_start} – ${selected.meeting_end}`} />
                                    </>
                                )}
                                {selected.message ? <KV k="Message" v={selected.message} /> : null}

                                <section>
                                    <div className="text-[0.62rem] tracking-[0.24em] uppercase font-bold text-brand-gold mb-3">Statut</div>
                                    <div className="grid grid-cols-2 gap-2" data-testid="status-picker">
                                        {["pending", "confirmed", "completed", "cancelled"].map((s) => (
                                            <button key={s} type="button" onClick={() => setDraft((d) => ({ ...d, status: s }))} data-testid={`status-${s}`}
                                                className={`px-3 py-2.5 text-[0.7rem] tracking-[0.2em] uppercase font-semibold border ${draft.status === s ? `${STATUS_META[s].color} border-transparent` : "bg-white text-brand-navy border-gray-200 hover:border-brand-gold"}`}>
                                                {labelStatus(s)}
                                            </button>
                                        ))}
                                    </div>
                                </section>
                                <section>
                                    <div className="text-[0.62rem] tracking-[0.24em] uppercase font-bold text-brand-gold mb-3">{t("bookings_admin.admin_note")}</div>
                                    <textarea value={draft.admin_note} onChange={(e) => setDraft((d) => ({ ...d, admin_note: e.target.value }))} rows={4} placeholder={t("bookings_admin.admin_note_placeholder")} data-testid="admin-note"
                                        className="w-full border border-gray-200 px-4 py-3 text-sm text-brand-navy focus:outline-none focus:border-brand-gold resize-y min-h-[110px]" />
                                </section>
                                <div className="flex flex-wrap gap-3">
                                    <button onClick={saveDrawer} data-testid="drawer-save" className="inline-flex items-center gap-2 px-6 py-3 bg-brand-gold hover:bg-brand-gold-deep text-white text-[0.7rem] tracking-[0.22em] uppercase font-semibold">
                                        <Save className="w-4 h-4" /> {t("inquiries.save")}
                                    </button>
                                    {waUrl(selected) ? (
                                        <a href={waUrl(selected)} target="_blank" rel="noreferrer" data-testid="drawer-wa" className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] hover:bg-[#1ebe57] text-white text-[0.7rem] tracking-[0.22em] uppercase font-semibold">
                                            <MessageCircle className="w-4 h-4" /> {t("bookings_admin.wa_click")}
                                        </a>
                                    ) : null}
                                </div>
                            </div>
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>
        </AdminLayout>
    );
}

function Sel({ label, value, onChange, options, testid }) {
    return (
        <label className="flex flex-col gap-1.5">
            <span className="text-[0.65rem] tracking-[0.22em] uppercase font-bold text-brand-navy inline-flex items-center gap-1"><Filter className="w-3 h-3" /> {label}</span>
            <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="bg-white border border-gray-200 px-4 py-2.5 text-sm text-brand-navy focus:outline-none focus:border-brand-gold min-w-[150px]">
                {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
        </label>
    );
}

function KV({ k, v }) {
    return (
        <div className="flex items-start justify-between gap-4 text-sm border-b border-gray-100 py-2">
            <span className="text-gray-500 uppercase text-[0.62rem] tracking-[0.2em] font-semibold min-w-[110px]">{k}</span>
            <span className="text-brand-navy text-right flex-1">{v}</span>
        </div>
    );
}
